import { isFixedTile } from '@/challenges/editing';
import { ownTileMetaAssignment } from '@/models/tiles/SignalSides';
import { runtimeGraphIds } from '@/models/logic/RuntimeGraphIds';
import { getChipPlacementResult } from '@/utils/chipPlacement';
import { Action, ActionType } from '@/models/HistoryModel';
import { Point } from '@/models/Point';
import { TileInstance } from '@/models/tiles/TileInstance';
import { deepClone } from '@/utils/clone';
import type {
  IChipEditorModel,
  IChipModel,
  ICircuitModel,
  IConnectionModel,
  ITileManagerModel,
} from '@/interfaces/models';
import type { Connection, ConnectionPoint } from '@/models/connections/ConnectionGraph';
import { CircuitType, Side, sideOffsets } from '@/schemas/common';
import { isInterfaceTile } from '@/models/tiles/TileRegistry';
import type { InterfaceTileState } from '@/schemas/tiles';
import { ChipInstanceModel } from '@/models/ChipInstanceModel';
import Context from '@/context';
import { createRemoveTilesAction } from '@/actions/tileActions';
import { LogicGraph } from '@/models/logic/LogicGraph';
import { Rotation, composeRotation, rotateSide } from '@/models/data/Rotation';
import { resolveConnectionSides } from '@/models/logic/utils';
import { getEffectiveSideIO } from '@/models/tiles/TileDefinition';
import { tileDefinitionRegistry } from '@/models/tiles/TileRegistry';
import { TileType } from '@/models/TileTypes';
import { TileSideIOType } from '@/schemas/tiles';
import { InterfaceType } from '@/models/ChipModel';
import type { InterfaceLinkInstanceId } from '@/types/ids';

/** Connect placed chip boundary tiles to adjacent external tiles using rotation-aware sides. */
export function connectChipBoundaryTiles(
  placedTiles: TileInstance[],
  tileManager: ITileManagerModel,
  connectionModel: IConnectionModel,
): Array<{ a: ConnectionPoint; b: ConnectionPoint }> {
  const connections: Array<{ a: ConnectionPoint; b: ConnectionPoint }> = [];
  const placedTileIds = new Set(placedTiles.map((t) => t.id));
  for (const placedTile of placedTiles) {
    for (const side of Object.values(Side)) {
      const offset = sideOffsets[side];
      const neighborPos = new Point(
        placedTile.location.x + offset.dx,
        placedTile.location.y + offset.dy,
      );
      const neighbor = tileManager.get(neighborPos);
      if (!neighbor || placedTileIds.has(neighbor.id)) continue;

      const resolved = resolveConnectionSides(placedTile, neighbor);
      if (!resolved) continue;

      // Touching optional pins are separate nets. Only wires or a forced
      // facing side opt into automatic boundary connections.
      const placedDef = tileDefinitionRegistry.get(placedTile.tileType);
      const neighborDef = tileDefinitionRegistry.get(neighbor.tileType);
      const placedIO = getEffectiveSideIO(placedTile, resolved.sideA, placedDef);
      const neighborIO = getEffectiveSideIO(neighbor, resolved.sideB, neighborDef);

      if (
        placedIO.type === TileSideIOType.NONE ||
        neighborIO.type === TileSideIOType.NONE
      )
        continue;
      const touchesWire =
        placedTile.tileType === TileType.Wire || neighbor.tileType === TileType.Wire;
      if (!touchesWire && !placedIO.forceConnection && !neighborIO.forceConnection)
        continue;

      const a = { tileId: placedTile.id, side: resolved.sideA };
      const b = { tileId: neighbor.id, side: resolved.sideB };
      connectionModel.connect(a, b);
      connections.push({ a, b });
    }
  }
  return connections;
}

export interface PlaceChipActionParams {
  context: Context;
  chipToPlace: IChipModel;
  anchorPosition: Point;
  targetCircuitModel: ICircuitModel;
  targetChipEditor: IChipEditorModel;
  rotation?: Rotation;
}

export const createPlaceChipAction = (params: PlaceChipActionParams): Action | null => {
  const {
    context,
    chipToPlace,
    anchorPosition,
    targetCircuitModel,
    targetChipEditor,
    rotation = Rotation.CW_0,
  } = params;

  const targetTileManager = targetCircuitModel.tileManagerModel;
  const targetConnectionModel = targetCircuitModel.connectionModel;
  const plan = () =>
    getChipPlacementResult({
      context,
      chip: chipToPlace,
      anchor: anchorPosition,
      rotation,
      tileManager: targetTileManager,
      circuitType: targetCircuitModel.circuitType,
      editingChip: targetChipEditor.chipModel,
    });
  const placement = plan();
  if (!placement.allowed) return null;
  const plannedTiles = placement.tiles;

  const sourceConnections =
    chipToPlace.structureCircuit.circuitModel.connectionModel.dto?.connections ?? [];

  let placedTiles: TileInstance[] = [];
  let appliedConnections: Connection[] = [];
  let placedChipInstance: ChipInstanceModel | null = null;

  return {
    type: ActionType.AddTiles,
    description: 'Place Chip',
    do: () => {
      // Actions can wait for worker authority. Recheck before touching any state.
      const current = plan();
      if (!current.allowed) throw new Error(current.reason ?? 'Chip cannot be placed');
      if (
        current.tiles.length !== plannedTiles.length ||
        current.tiles.some(
          (entry, i) =>
            entry.source !== plannedTiles[i].source ||
            !entry.location.equals(plannedTiles[i].location),
        )
      )
        throw new Error('Chip structure changed before placement');
      placedTiles = [];
      appliedConnections = [];

      const chipInstanceId = targetChipEditor.chipModel.editingInstance.id;

      if (!chipToPlace.templateGraph) {
        chipToPlace.rebuildTemplateGraph();
      }
      const cloneResult = chipToPlace.templateGraph?.clone();
      const logicGraph = cloneResult?.graph ?? new LogicGraph(runtimeGraphIds(context));

      placedChipInstance = new ChipInstanceModel(
        context,
        chipToPlace.id,
        anchorPosition,
        logicGraph,
        {
          chipInstanceId,
          location: anchorPosition,
          circuitType: targetCircuitModel.circuitType,
        },
      );

      if (cloneResult) {
        placedChipInstance.templateToInstanceIdMap = cloneResult.idMap;
      }
      placedChipInstance.rotation = rotation;

      targetCircuitModel.chips.push(placedChipInstance);
      context.project?.chipInstanceMap?.set(placedChipInstance.id, placedChipInstance);
      targetChipEditor.chipModel.logicCircuit.chipInstances.set(
        placedChipInstance.id,
        placedChipInstance,
      );

      const placedTileBySourceId = new Map<string, TileInstance>();

      for (const { source, location } of plannedTiles) {
        const placedTile = new TileInstance(context, source.tileType, {
          chipInstanceId,
          location,
          circuitType: targetCircuitModel.circuitType,
        });

        const meta = deepClone(source.meta);
        meta.partOfChip = placedChipInstance.id;
        meta.rotation = composeRotation(meta.rotation ?? Rotation.CW_0, rotation);
        placedTile.meta = ownTileMetaAssignment(placedTile, meta);
        placedTile.state = deepClone(source.state) as typeof placedTile.state;

        if (
          isInterfaceTile(placedTile) ||
          (chipToPlace.kind === 'bundle-adapter' &&
            placedTile.tileType === TileType.BundleInterface &&
            (placedTile.state as InterfaceTileState<unknown>).connectedInterface)
        ) {
          const interfaceState = placedTile.state as InterfaceTileState<unknown>;
          interfaceState.connectedInterface.chipInstanceId = placedChipInstance.id;
          interfaceState.connectedInterface.circuitType = CircuitType.ChipLogic;
        }

        if (chipToPlace.kind === 'bundle-adapter' && source.effectiveIoSides)
          placedTile.effectiveIoSides = deepClone(source.effectiveIoSides);
        if (
          chipToPlace.kind === 'bundle-adapter' &&
          placedTile.tileType === TileType.BundleInterface
        )
          placedTile.effectiveIoSides = {
            ...tileDefinitionRegistry.get(TileType.BundleInterface).ioSides,
            [Side.RIGHT]: { type: TileSideIOType.NONE, forceConnection: false },
          };

        targetTileManager.add(placedTile);
        placedTiles.push(placedTile);
        placedTileBySourceId.set(source.id, placedTile);
      }

      for (const connection of sourceConnections) {
        const tileA = placedTileBySourceId.get(connection.a.tileId);
        const tileB = placedTileBySourceId.get(connection.b.tileId);

        if (!tileA || !tileB) {
          continue;
        }

        const pointA = {
          tileId: tileA.id,
          side: rotateSide(connection.a.side, rotation),
        };
        const pointB = {
          tileId: tileB.id,
          side: rotateSide(connection.b.side, rotation),
        };

        // Use addConnection directly — connect() validates geometry with unrotated
        // offsets which fails for rotated tiles. Chip-internal connections are
        // known-correct from the chip's structure circuit.
        targetConnectionModel.addConnection(pointA, pointB);
        appliedConnections.push({ a: pointA, b: pointB });
      }

      // Sync effectiveIoSides on placed MI tiles from the chip's InterfaceLink sideConfig
      for (const placedTile of placedTiles) {
        if (placedTile.tileType === TileType.MultiInterface) {
          const ifState = placedTile.state as InterfaceTileState<unknown>;
          const linkId = ifState.connectedInterface
            ?.interfaceLinkId as InterfaceLinkInstanceId;
          if (linkId) {
            const link = chipToPlace.interfaceLinkMap.get(linkId);
            if (link && link.type === InterfaceType.MULTI) {
              placedTile.effectiveIoSides = link.computeEffectiveIoSides();
            }
          }
        }
      }

      // Connect chip boundary tiles to adjacent external tiles.
      const boundaryConnections = connectChipBoundaryTiles(
        placedTiles,
        targetTileManager,
        targetConnectionModel,
      );
      appliedConnections.push(...boundaryConnections);
      if (placedTiles.length) context.app?.playSound?.('place');
    },
    undo: () => {
      for (const connection of appliedConnections) {
        targetConnectionModel.disconnect(connection.a, connection.b);
      }
      appliedConnections = [];

      for (const placedTile of placedTiles) {
        targetTileManager.remove(placedTile);
      }
      placedTiles = [];

      if (placedChipInstance) {
        const index = targetCircuitModel.chips.indexOf(placedChipInstance);
        if (index >= 0) {
          targetCircuitModel.chips.splice(index, 1);
        }
        targetChipEditor.chipModel.logicCircuit.chipInstances.delete(
          placedChipInstance.id,
        );
        context.project?.chipInstanceMap?.delete(placedChipInstance.id);
        placedChipInstance = null;
      }
    },
  };
};

export interface RemoveChipInstanceActionParams {
  context: Context;
  chipInstanceId: string;
  targetCircuitModel: ICircuitModel;
  targetChipEditor: IChipEditorModel;
}

export const createRemoveChipInstanceAction = (
  params: RemoveChipInstanceActionParams,
): Action | null => {
  const { context, chipInstanceId, targetCircuitModel, targetChipEditor } = params;

  const tileManager = targetCircuitModel.tileManagerModel;
  const connectionModel = targetCircuitModel.connectionModel;

  const tilesToRemove = tileManager.tiles.filter(
    (tile) => tile.meta.partOfChip === chipInstanceId,
  );

  if (tilesToRemove.length === 0 || tilesToRemove.some(isFixedTile)) {
    return null;
  }

  const chipInstance =
    targetCircuitModel.chips.find((instance) => instance.id === chipInstanceId) ?? null;

  const removeTilesAction = createRemoveTilesAction(context, tileManager, tilesToRemove, {
    connectionModel,
  });

  return {
    type: ActionType.RemoveTiles,
    description: 'Remove Chip Instance',
    do: () => {
      removeTilesAction.do();

      if (!chipInstance) {
        return;
      }

      const index = targetCircuitModel.chips.indexOf(chipInstance);
      if (index >= 0) {
        targetCircuitModel.chips.splice(index, 1);
      }

      targetChipEditor.chipModel.logicCircuit.chipInstances.delete(chipInstance.id);
      context.project?.chipInstanceMap?.delete(chipInstance.id);
    },
    undo: () => {
      if (chipInstance) {
        targetCircuitModel.chips.push(chipInstance);
        targetChipEditor.chipModel.logicCircuit.chipInstances.set(
          chipInstance.id,
          chipInstance,
        );
        context.project?.chipInstanceMap?.set(chipInstance.id, chipInstance);
      }

      removeTilesAction.undo();
    },
  };
};
